import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import useIdleMonitor from "./useIdleMonitor";
import { INACTIVE_TIMEOUT } from "../components/constants";

const AuthContext = createContext(null);
const apiUrl = process.env.REACT_APP_API_BASE_URL;

export let axiosInstance = axios.create({
    baseURL: apiUrl,
    headers: { Authorization: `Bearer ${localStorage.getItem("access_token")}`, "Content-Type": "application/json" },
});

export const AuthProvider = ({ children }) => {
    const navigate = useNavigate();
    const [token, setToken] = useState(localStorage.getItem("access_token"));
    const [twoFactorToken, setTwoFactorToken] = useState(localStorage.getItem("two_factor_token"));

    const handleLogout = useCallback(() => {
        localStorage.removeItem("access_token");
        localStorage.removeItem("two_factor_token");
        localStorage.removeItem("lastActiveTime");
        setToken(null);
    }, []);

    useIdleMonitor(handleLogout);

    useEffect(() => {
        const storedLoginTime = parseInt(localStorage.getItem("lastActiveTime"));

        if (storedLoginTime) {
            const now = Date.now();
            if (now - storedLoginTime > INACTIVE_TIMEOUT) {
                handleLogout();
            }
        }
    }, [handleLogout]);

    const handleLogin = useCallback(
        async (formData, origin) => {
            try {
                const { data } = await axios.post(`${apiUrl}/token`, formData);
                if (data.two_factor_required) {
                    localStorage.setItem("two_factor_token", data.two_factor_token);
                    setTwoFactorToken(data.two_factor_token);
                    navigate("/login/2fa-verify", { state: { origin } });
                    return;
                }
                const access_token = data.access_token;

                axiosInstance = axios.create({
                    baseURL: apiUrl,
                    headers: { Authorization: `Bearer ${access_token}`, "Content-Type": "application/json" },
                });

                //validate user access
                try {
                    await axiosInstance.get("/validate_pro");
                } catch (err) {
                    return "Oops! Looks like you are logging into the wrong tool. Please try at Music Test by RadioAnalyzer.";
                }

                //set JWT token to local
                localStorage.setItem("access_token", access_token);
                localStorage.setItem("lastActiveTime", Date.now());
                setToken(access_token);

                if (origin) navigate(origin);
            } catch (err) {
                handleLogout();
                return "Woops! Looks like you posted the wrong credentials.";
            }
        },
        [handleLogout, navigate]
    );

    const handleTwoFactorLogin = useCallback(
        async (code, origin) => {
            try {
                if (!twoFactorToken) return "Two-factor authentication token is missing. Please login again.";

                const res = await axios.post(`${apiUrl}/2fa/verify`, { two_factor_token: twoFactorToken, code });
                const access_token = res.data.access_token;

                axiosInstance = axios.create({
                    baseURL: apiUrl,
                    headers: { Authorization: `Bearer ${access_token}`, "Content-Type": "application/json" },
                });

                //validate user access
                try {
                    await axiosInstance.get("/validate_pro");
                } catch (err) {
                    return "Oops! Looks like you are logging into the wrong tool. Please try at Music Test by RadioAnalyzer.";
                }

                localStorage.setItem("access_token", access_token);
                localStorage.setItem("lastActiveTime", Date.now());
                localStorage.removeItem("two_factor_token");
                setToken(access_token);

                if (origin) navigate(origin);
            } catch (err) {
                return err.response?.data?.detail || "An error occurred during two-factor authentication. Please try again.";
            }
        },
        [navigate, twoFactorToken]
    );

    const value = useMemo(
        () => ({
            token,
            onLogin: handleLogin,
            onLogout: handleLogout,
            onTwoFactorLogin: handleTwoFactorLogin,
        }),
        [handleLogin, handleLogout, handleTwoFactorLogin, token]
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);

export const getLatestBlogs = async () => {
    const API_KEY = process.env.REACT_APP_STRAPI_API_KEY;
    const IDENTIFIER = process.env.REACT_APP_STRAPI_ADMIN_EMAIL;
    const PASSWORD = process.env.REACT_APP_STRAPI_ADMIN_PASSWORD;
    const BACKEND = process.env.REACT_APP_WEBSITE_URL_BACKEND;

    try {
        if (!BACKEND) return [];

        const response = await axios.get(`${BACKEND}/api/blogs?populate=*`, {
            auth: {
                username: IDENTIFIER,
                password: PASSWORD,
            },
            headers: {
                Authorization: `Bearer ${API_KEY}`,
                "Content-Type": "application/json",
            },
        });

        const blogs = Array.isArray(response?.data?.data) ? response.data.data : [];
        const latestBlogs = blogs
            .filter(blog => blog && typeof blog === "object")
            .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
            .slice(0, 3);

        return latestBlogs;
    } catch (error) {
        console.error("Failed to load latest blogs:", error);
        return [];
    }
};
